import { Select, Skeleton, Tooltip } from "antd";
import React from "react";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import type { ScheduleSlot } from "../../service/schedulePick.service";
import { EventType, trackEvent } from "../../utils/analytics";
import { resolveClinicZone, scheduleSlotLabel } from "../../utils/clientSchedule";
import { isSuggestionApplied } from "../../utils/sessionTemplate";
import { LOOK_AHEAD_MINUTES, VISIBLE_SLOTS } from "./quickStart";
import { ChevronDownIcon, ChevronUpIcon, ClockIcon, CloseIcon, TemplateIcon, ZapIcon } from "./quickStartIcons";
import styles from "./quickstart.module.scss";
import type { QuickStartSchedule, SessionTemplateState } from "./useQuickStart";

/**
 * "Suggested · New Patient H&P" under a patient whose visit type names a template. Hidden once the
 * clinician picks on the card: that pick is what every row uses.
 */
const suggestionCaption = (
  slot: ScheduleSlot,
  session: SessionTemplateState,
  minConfidence?: number,
): string | null => {
  const suggestion = slot.template_suggestion;
  if (!suggestion || session.hasChosen) return null;
  return isSuggestionApplied(suggestion, minConfidence)
    ? `Suggested · ${suggestion.template_name}`
    : `Suggested · ${suggestion.template_name} (low confidence)`;
};

/** "Template  Family Medicine SOAP ⌄": the template every Start on the card uses (a row's suggestion aside), and its picker. */
export const TemplatePicker = ({ session }: { session: SessionTemplateState }) => {
  const { template } = session;
  // The hint must not sit over the open list of templates.
  const [pickerOpen, setPickerOpen] = React.useState(false);
  if (!session.ready) {
    return <Skeleton.Button active block className={styles.Template__loading} />;
  }
  return (
    <Tooltip
      title={template ? null : "No template on file yet. Start now and choose one when you submit, or pick it here."}
      open={pickerOpen ? false : undefined}
    >
      <Select
        variant="borderless"
        showSearch
        data-testid="quick-start-template"
        className={`${styles.Template} ${template ? "" : styles["Template--pending"]}`}
        popupMatchSelectWidth
        prefix={
          <span className={styles.Template__prefix}>
            <TemplateIcon />
            <span className={styles.Template__label}>Template</span>
          </span>
        }
        suffixIcon={<ChevronDownIcon />}
        value={template?.value}
        placeholder="Choose"
        options={session.groups}
        optionFilterProp="searchText"
        labelRender={() => template?.name ?? "Choose"}
        onOpenChange={(open) => {
          setPickerOpen(open);
          if (open) session.loadGallery();
        }}
        onChange={(value) => {
          const option = session.options.find((candidate) => candidate.value === value);
          if (option) session.choose(option);
        }}
      />
    </Tooltip>
  );
};

type QuickStartCardProps = {
  schedule: QuickStartSchedule;
  session: SessionTemplateState;
  /** The patient whose recording is starting, while it starts. */
  startingId: number | null;
  onQuickStart: (slot: ScheduleSlot, context: { expanded: boolean }) => void;
  /** × : the schedule's dismiss, wrapped by the caller so it can move focus. */
  onDismiss: () => void;
  dismissRef?: React.Ref<HTMLButtonElement>;
  footer: React.ReactNode;
};

/**
 * "Who's in the room?": today's schedule from an hour ago to two hours ahead, in appointment order,
 * each patient one click from recording with the template shown under the list. Three rows around
 * now, the rest behind "Show N more" in a list that scrolls. × closes it and the card becomes
 * Start Encounter.
 */
const QuickStartCard = ({
  schedule,
  session,
  startingId,
  onQuickStart,
  onDismiss,
  dismissRef,
  footer,
}: QuickStartCardProps) => {
  const { sessionTerm, sessionTermLower } = useSpecialityReference();
  const zone = React.useMemo(() => resolveClinicZone(schedule.pick?.clinic_tz), [schedule.pick?.clinic_tz]);
  const [expanded, setExpanded] = React.useState(false);
  // Each Start is described by its row's time and provider: two patients can share a name.
  const rowId = React.useId();
  // The expanded list scrolls past ~5 rows; a fade marks that there is more above or below.
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [scroll, setScroll] = React.useState({ scrollable: false, atStart: true, atEnd: true });
  const measure = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const next = {
      scrollable: el.scrollHeight > el.clientHeight + 1,
      atStart: el.scrollTop <= 8,
      atEnd: el.scrollTop + el.clientHeight >= el.scrollHeight - 8,
    };
    setScroll((prev) =>
      prev.scrollable === next.scrollable && prev.atStart === next.atStart && prev.atEnd === next.atEnd ? prev : next,
    );
  }, []);

  const { view } = schedule;
  const slots = view.kind === "list" ? view.slots : [];
  const first = view.kind === "list" ? view.first : 0;
  const more = slots.length - VISIBLE_SLOTS;
  const open = expanded && more > 0;
  // Expanding keeps the same patients at the top: the earlier appointments are a scroll up.
  // biome-ignore lint/correctness/useExhaustiveDependencies: only when the list opens, not as "first" drifts
  React.useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !open) return;
    const row = el.children[first] as HTMLElement | undefined;
    if (row) el.scrollTop = row.offsetTop;
  }, [open]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-measure when the rows change
  React.useLayoutEffect(measure, [measure, open, slots.length]);

  if (view.kind !== "list") return null;
  const rows = open ? slots : slots.slice(first, first + VISIBLE_SLOTS);
  // A colleague's patient names its provider; once any row does, every row does, so the list reads evenly.
  const namesProviders = slots.some((slot) => slot.client.is_owned === false);
  const busy = startingId !== null || !session.ready;

  const toggle = () => {
    if (!open) trackEvent(EventType.QUICK_START_LIST_EXPANDED, { candidates: slots.length });
    setExpanded(!open);
  };

  return (
    <div className={styles.Card} data-testid="quick-start-card">
      <div className={styles.Room__top}>
        <div className={styles.Room__chip} data-testid="quick-start-count">
          <ClockIcon />
          {slots.length} booked in the next {LOOK_AHEAD_MINUTES / 60} hours
        </div>
        <Tooltip title={`None of these? Close the list and use Start ${sessionTerm}`} trigger={["hover", "focus"]}>
          <button
            type="button"
            ref={dismissRef}
            aria-label="Hide schedule suggestions"
            className={styles.Room__dismiss}
            disabled={startingId !== null}
            onClick={onDismiss}
            data-testid="quick-start-dismiss"
          >
            <CloseIcon />
          </button>
        </Tooltip>
      </div>

      <div className={styles.Room__title} data-testid="quick-start-title">
        Who's in the room?
      </div>

      <div className={styles.Room__list}>
        <div className={styles.Room__scrollWrap}>
          <div
            ref={scrollRef}
            onScroll={measure}
            className={[
              styles.Room__rows,
              open ? styles["Room__rows--expanded"] : "",
              open && scroll.scrollable ? styles["Room__rows--scrollable"] : "",
            ].join(" ")}
            data-testid="quick-start-candidates"
          >
            {rows.map((slot) => {
              const time = scheduleSlotLabel(slot.scheduled_at, zone);
              const provider = namesProviders ? slot.client.owner_name : null;
              const caption = suggestionCaption(slot, session, schedule.pick?.template_min_confidence);
              return (
                <div
                  key={slot.client.id}
                  className={`${styles.Room__row} ${caption ? styles["Room__row--suggested"] : ""}`}
                  data-testid="quick-start-candidate"
                >
                  <div className={styles.Room__text}>
                    <div className={styles.Room__name} title={slot.client.name}>
                      {slot.client.name}
                    </div>
                    <div className={styles.Room__meta} id={`${rowId}-${slot.client.id}`}>
                      {[time, provider].filter(Boolean).join(" · ")}
                    </div>
                    {caption && (
                      <div className={styles.Room__suggestion} title={caption} data-testid="quick-start-suggestion">
                        {caption}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    className={styles.Room__start}
                    aria-label={`Start ${sessionTermLower} with ${slot.client.name}`}
                    aria-describedby={`${rowId}-${slot.client.id}`}
                    disabled={busy}
                    onClick={() => onQuickStart(slot, { expanded: open })}
                    data-testid="quick-start-start"
                  >
                    <ZapIcon />
                    {startingId === slot.client.id ? "Starting…" : "Start"}
                  </button>
                </div>
              );
            })}
          </div>
          {open && scroll.scrollable && !scroll.atStart && (
            <div className={`${styles.Room__fade} ${styles["Room__fade--top"]}`} />
          )}
          {open && scroll.scrollable && !scroll.atEnd && <div className={styles.Room__fade} />}
        </div>
        {more > 0 && (
          <div className={styles.Room__moreRow}>
            <button
              type="button"
              className={styles.Room__more}
              aria-expanded={open}
              onClick={toggle}
              data-testid="quick-start-more"
            >
              {open ? "Show fewer" : `Show ${more} more`}
              {open ? <ChevronUpIcon /> : <ChevronDownIcon />}
            </button>
          </div>
        )}
      </div>

      <TemplatePicker session={session} />
      {footer}
    </div>
  );
};

export default QuickStartCard;
